import React from 'react';

import { Box, Button, radioClasses, Typography } from '@mui/material';
import { useNavigate } from 'react-router';

import { Field, Form, FormSpy } from '@hrlogics/react-components/form';
import { SelectFieldOption } from '@hrlogics/react-helper-types';

import {
  appRoutes,
  EMPLOYERS_DEFAULT_PIN_SETTINGS_FORM_VALUES,
  RouterEntitiesId,
} from '@clearverify/constants';

import { PeoPayrollProviderCompanyType, SignUpMethod } from 'src/api';
import { RadioGroupField, SelectField, TextField } from 'src/components/finalForm';
import { SIGN_UP_METHOD_OPTIONS } from 'src/constants/employer';
import {
  usePeoPayrollProviderById,
  usePeoPayrollProviderParamId,
} from 'src/hooks/peoPayrollProvider';
import { useCurrentUserPermissions } from 'src/hooks/user';
import type { SxProp } from 'src/types/mui';
import { buildUrl } from 'src/utils/router';

import { PeoPPLiveStatusSettings } from './components';
import {
  PeoPayrollProviderSettingsFormFields,
  PeoPayrollProviderSettingsFormValues,
} from './types';
import { peoPayrollProviderSettingsFormValidationSchema } from './utils';

export const PeoPayrollProviderSettingsForm: React.FC = () => {
  const navigate = useNavigate();

  const peoPayrollProviderId = usePeoPayrollProviderParamId();
  const { peoPayrollProvider, updatePeoPayrollProvider } =
    usePeoPayrollProviderById(peoPayrollProviderId);

  const { isPlatformAdmin } = useCurrentUserPermissions();

  const initialValues = React.useMemo(
    (): Partial<PeoPayrollProviderSettingsFormValues> => ({
      [PeoPayrollProviderSettingsFormFields.companyType]: peoPayrollProvider?.companyType,
      [PeoPayrollProviderSettingsFormFields.signUpMethod]: peoPayrollProvider?.signUpMethod,
      [PeoPayrollProviderSettingsFormFields.pinHint]: peoPayrollProvider?.pinHint,
    }),
    [
      peoPayrollProvider?.companyType,
      peoPayrollProvider?.signUpMethod,
      peoPayrollProvider?.pinHint,
    ],
  );

  const onSubmit = React.useCallback(
    async (formData: PeoPayrollProviderSettingsFormValues) => {
      try {
        await updatePeoPayrollProvider({
          data: {
            companyType: formData.companyType,
            signUpMethod: formData.signUpMethod,
            pinHint: formData.signUpMethod === SignUpMethod.Pin ? (formData.pinHint ?? null) : null,
            ...EMPLOYERS_DEFAULT_PIN_SETTINGS_FORM_VALUES,
          },
        });

        if (formData.companyType !== initialValues.companyType) {
          const urlPattern =
            formData.companyType === PeoPayrollProviderCompanyType.PeoPayrollProvider
              ? appRoutes.peoPayrollProviders.detail.settings
              : appRoutes.partnerships.detail.peoPayrollProvider.settings;
          navigate(
            buildUrl(urlPattern, { [RouterEntitiesId.peoPayrollProvider]: peoPayrollProviderId }),
          );
        }
      } catch (error) {
        console.error(error);
      }
    },
    [peoPayrollProviderId, initialValues.companyType, navigate, updatePeoPayrollProvider],
  );

  return (
    <Form<PeoPayrollProviderSettingsFormValues>
      onSubmit={onSubmit}
      initialValues={initialValues}
      subscription={{ dirty: true, submitting: true }}
      validationSchema={peoPayrollProviderSettingsFormValidationSchema}
    >
      {({ form, dirty, submitting }) => (
        <Box sx={boxStyles} rowGap={3}>
          <React.Activity mode={isPlatformAdmin ? 'visible' : 'hidden'}>
            <Box sx={settingsStyles}>
              <Typography variant="subtitle1" fontSize={16}>
                Organization Type
              </Typography>

              <Typography variant="body1" color="text.secondary" mt={1}>
                Specify whether this entity is a PEO Payroll Provider, or a Partnership. Unlike PEO
                Payroll Provider users, Partnership entity users do not own the data and are not
                able to view employment data and verifications.
              </Typography>

              <Box mt={2}>
                <Field
                  label="Organization Type"
                  name={PeoPayrollProviderSettingsFormFields.companyType}
                  fullWidth
                  disabled={submitting}
                  component={SelectField}
                  options={companyTypeSelectFieldValues}
                />
              </Box>
            </Box>
            <PeoPPLiveStatusSettings />
          </React.Activity>

          <Box sx={settingsStyles}>
            <Typography variant="subtitle1" fontSize={16}>
              Employee Sign Up PIN
            </Typography>
            <Typography variant="body1" color="text.secondary" mt={1}>
              Employees will use this 8 digit PIN as the first part of the sign-up process to
              confirm their identity. <br /> This is an optional additional layer of security.
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Format:
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={titleDataSchemaStyles}>
              XXXX-YYYY where XXXX - 4 last digits of SSN, YYYY - year of birth
            </Typography>

            <Field
              fullWidth
              name={PeoPayrollProviderSettingsFormFields.signUpMethod}
              component={RadioGroupField}
              options={SIGN_UP_METHOD_OPTIONS}
              disabled={submitting}
              sx={radioFieldStyles}
            />

            <FormSpy<PeoPayrollProviderSettingsFormValues> subscription={{ values: true }}>
              {({ values }) => {
                if (values.signUpMethod === SignUpMethod.Pin) {
                  return (
                    <Box>
                      <Typography variant="body1" color="text.secondary" mt={1} mb={2}>
                        Enter your PIN hint for employees here (optional, 200 characters max)
                      </Typography>

                      <Field
                        name={PeoPayrollProviderSettingsFormFields.pinHint}
                        label="PIN Hint"
                        component={TextField}
                        disabled={submitting}
                        fullWidth
                      />
                    </Box>
                  );
                }
                return null;
              }}
            </FormSpy>
          </Box>

          <Box display="flex" justifyContent="center" alignItems="center" gap={2}>
            <Button
              color="error"
              disabled={!dirty}
              size="medium"
              type="reset"
              variant="outlined"
              onClick={() => form.restart()}
            >
              Reset
            </Button>
            <Button disabled={!dirty} loading={submitting} variant="contained" type="submit">
              Save changes
            </Button>
          </Box>
        </Box>
      )}
    </Form>
  );
};

const companyTypeSelectFieldValues: SelectFieldOption<PeoPayrollProviderCompanyType>[] = [
  { label: 'PEO Payroll Provider', value: PeoPayrollProviderCompanyType.PeoPayrollProvider },
  { label: 'Partnership', value: PeoPayrollProviderCompanyType.Partnership },
];

const radioFieldStyles: SxProp = {
  marginLeft: 1,
  [`& .${radioClasses.checked}`]: {
    borderColor: 'red',
  },
  [`& .${radioClasses.root}`]: {
    fontSize: '1rem',
  },
};

const boxStyles: SxProp = {
  display: 'flex',
  flexDirection: 'column',
};

const settingsStyles: SxProp = {
  border: '1px solid rgba(0, 0, 0, 0.12)',
  borderRadius: '8px',
  padding: '20px 24px',
};

const titleDataSchemaStyles: SxProp = {
  borderBottom: '1px solid #D9D9D9',
  paddingBottom: '16px',
  marginBottom: '16px',
};
